import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { DragDropContext } from '@hello-pangea/dnd';
import Client from '../api/client';

const DragDropStateContext = createContext(null);

export const useDragDropState = () => {
  const context = useContext(DragDropStateContext);
  if (!context) {
    throw new Error('useDragDropState must be used within a DragDropProvider');
  }
  return context;
};

export const DragDropProvider = ({ children }) => {
  const [isDraggingBookmark, setIsDraggingBookmark] = useState(false);
  const [draggingBookmarkId, setDraggingBookmarkId] = useState(null);
  const [draggingFromGroupId, setDraggingFromGroupId] = useState(null);
  const [hoveredGroupTabId, setHoveredGroupTabId] = useState(null);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Refs for callbacks that components register
  const bookmarkReorderHandlerRef = useRef(null);
  const bookmarkMoveHandlerRef = useRef(null);
  const groupReorderHandlerRef = useRef(null);
  const groupTabsRef = useRef([]); // Store references to group tab elements

  // Register handlers from child components
  const registerBookmarkReorderHandler = useCallback(handler => {
    bookmarkReorderHandlerRef.current = handler;
  }, []);

  const registerBookmarkMoveHandler = useCallback(handler => {
    bookmarkMoveHandlerRef.current = handler;
  }, []);

  const registerGroupReorderHandler = useCallback(handler => {
    groupReorderHandlerRef.current = handler;
  }, []);

  // Register group tabs for hit detection
  const registerGroupTab = useCallback((groupId, element) => {
    if (element) {
      groupTabsRef.current = groupTabsRef.current.filter(
        t => t.groupId !== groupId
      );
      groupTabsRef.current.push({ groupId, element });
    } else {
      groupTabsRef.current = groupTabsRef.current.filter(
        t => t.groupId !== groupId
      );
    }
  }, []);

  // Track mouse position during bookmark drag
  useEffect(() => {
    if (!isDraggingBookmark) {
      setHoveredGroupTabId(null);
      return;
    }

    const handleMouseMove = e => {
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      let foundTabId = null;
      for (const { groupId, element } of groupTabsRef.current) {
        if (groupId === draggingFromGroupId) continue; // Skip source group

        const rect = element.getBoundingClientRect();
        if (
          mouseX >= rect.left &&
          mouseX <= rect.right &&
          mouseY >= rect.top &&
          mouseY <= rect.bottom
        ) {
          foundTabId = groupId;
          break;
        }
      }
      setHoveredGroupTabId(foundTabId);
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [isDraggingBookmark, draggingFromGroupId]);

  const onDragStart = useCallback(start => {
    const { draggableId, source } = start;

    // Check if we're dragging a bookmark (format: "bookmark-{id}")
    if (draggableId.startsWith('bookmark-')) {
      setIsDraggingBookmark(true);
      const bookmarkId = parseInt(draggableId.split('-')[1]);
      setDraggingBookmarkId(bookmarkId);

      // Extract group ID from droppableId (format: "bookmark-grid-{groupId}")
      if (source.droppableId.startsWith('bookmark-grid-')) {
        const groupId = parseInt(source.droppableId.split('-')[2]);
        setDraggingFromGroupId(groupId);
      }
    }
  }, []);

  const onDragEnd = useCallback(
    result => {
      const wasOverGroupTab = hoveredGroupTabId;
      const bookmarkId = draggingBookmarkId;
      const sourceGroupId = draggingFromGroupId;

      // Reset dragging state
      setIsDraggingBookmark(false);
      setDraggingBookmarkId(null);
      setDraggingFromGroupId(null);
      setHoveredGroupTabId(null);

      const { source, destination, draggableId } = result;

      // Handle bookmark drag
      if (draggableId.startsWith('bookmark-')) {
        // Check if dropped over a group tab (using our mouse tracking)
        if (wasOverGroupTab && wasOverGroupTab !== sourceGroupId) {
          // Move bookmark to new group
          new Client()
            .moveBookmark(sourceGroupId, bookmarkId, wasOverGroupTab)
            .then(() => {
              // Notify the source group to remove the bookmark
              if (bookmarkMoveHandlerRef.current) {
                bookmarkMoveHandlerRef.current(
                  bookmarkId,
                  sourceGroupId,
                  wasOverGroupTab
                );
              }
            })
            .catch(error => {
              console.error('Error moving bookmark:', error);
            });

          return;
        }

        // Check if dropped in a bookmark grid (reordering within same group)
        if (
          destination &&
          destination.droppableId.startsWith('bookmark-grid-')
        ) {
          const destGroupId = parseInt(destination.droppableId.split('-')[2]);
          const srcGroupId = parseInt(source.droppableId.split('-')[2]);

          // If same group, it's a reorder
          if (
            destGroupId === srcGroupId &&
            source.index !== destination.index
          ) {
            if (bookmarkReorderHandlerRef.current) {
              bookmarkReorderHandlerRef.current(
                bookmarkId,
                srcGroupId,
                source.index,
                destination.index
              );
            }
          }
        }

        return;
      }

      // Handle group tab reordering (draggableId is the group id as string)
      if (
        destination &&
        source.droppableId === 'group-tabs' &&
        destination.droppableId === 'group-tabs'
      ) {
        if (source.index !== destination.index) {
          if (groupReorderHandlerRef.current) {
            groupReorderHandlerRef.current(
              draggableId,
              source.index,
              destination.index
            );
          }
        }
      }
    },
    [hoveredGroupTabId, draggingBookmarkId, draggingFromGroupId]
  );

  const value = useMemo(
    () => ({
      isDraggingBookmark,
      draggingBookmarkId,
      draggingFromGroupId,
      hoveredGroupTabId,
      isSearchActive,
      setIsSearchActive,
      registerBookmarkReorderHandler,
      registerBookmarkMoveHandler,
      registerGroupReorderHandler,
      registerGroupTab,
    }),
    [
      isDraggingBookmark,
      draggingBookmarkId,
      draggingFromGroupId,
      hoveredGroupTabId,
      isSearchActive,
      registerBookmarkReorderHandler,
      registerBookmarkMoveHandler,
      registerGroupReorderHandler,
      registerGroupTab,
    ]
  );

  return (
    <DragDropStateContext.Provider value={value}>
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        {children}
      </DragDropContext>
    </DragDropStateContext.Provider>
  );
};

DragDropProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
